import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useMutation } from "@apollo/client";

import { Checkbox, FormGroup, Input } from "@cofano/cck";
import { Divider, Grid2 as Grid, IconButton, List, ListItem, ListItemText } from "@mui/material";
import ArrowRightAlt from "@mui/icons-material/ArrowRightAlt";
import Edit from "@mui/icons-material/Edit";

import { FormDialog } from "components/dialogs/Dialogs";
import CharterContractSelect, { CharterContractOption } from "components/contract/CharterContractSelect";

import CargoCreateEditDialog, { CargoDialogType } from "view/cargos/components/dialogs/CargoCreateEditDialog";
import useCreateOrExistingCharterFormFields, { charterDefaultValues, CreateOrExistingPurchaseCharterForm } from "view/charters/components/form/useCreateOrExistingCharterFormFields";

import { CargoLinkedCharter, Ship } from "types/index";
import { gql } from "__generated__/gql";
import { CargoPlanningDialogCargoFragment } from "__generated__/view/planning/graphql/planning.fragments.generated";
import PlanningChecks from "components/planningChecks/PlanningChecks";
import { useStoreState } from "ezStore";
import { hasPluginPermission } from "utils";

type CargoPlanningDialogProps = {
	open: boolean;
	onClose: () => void;
	refetch: () => void;
	onSave: (data: CargoLinkedCharter, createSalesContract: boolean) => void;
	cargos: CargoPlanningDialogCargoFragment[];
	ship: Pick<Ship, "id" | "name" | "owner" | "operator" | "defaultCharterSubtype">;
};

type CargoPlanningDialogForm = {
	voyageNumber?: string;
	salesContract?: CharterContractOption;
	createSalesContract?: boolean;
} & CreateOrExistingPurchaseCharterForm;

const CargoPlanningDialog = ({ open, onClose, onSave, cargos, ship, refetch }: CargoPlanningDialogProps) => {

	const session = useStoreState(state => state.session);
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ cargoWithIdDialog, setCargoWithIdDialog ] = useState<number | undefined>(undefined);
	const [ cargoList, setCargoList ] = useState<CargoPlanningDialogCargoFragment[]>(cargos);

	const { control, handleSubmit, reset, formState, setValue, watch } = useForm<CargoPlanningDialogForm>({
		defaultValues: {
			voyageNumber: undefined,
			charter: charterDefaultValues,
			salesContract: undefined,
		},
	});

	const { charterInput, charterFields, createContract } = useCreateOrExistingCharterFormFields({
		control: control,
		setValue: setValue,
		watch: watch,
		formState: formState,
		ship: ship,
		cargos: cargoList,
		labelPlacement: "top",
	});

	const [ planCargo ] = useMutation(PLAN_CARGO_ON_VOYAGE_MUTATION);

	const handleOnClose = () => {
		onClose();
		reset();
		setCargoList([]);
	};

	const getCargosDates = () => cargos
		.map(it => moment(it.firstLoadingAction?.start ?? null).valueOf())
		.filter((it: number) => Boolean(it))
		.sort((a, b) => a - b);

	const createSalesContract = watch("createSalesContract") && (createContract || !!charterInput.id);

	const save = (data: CargoPlanningDialogForm) => {

		setLoading(true);

		planCargo({
			variables: {
				input: {
					salesCharterIds: cargoList.map(it => it.charter?.id as number),
					voyageInput: {
						voyageNumber: data.voyageNumber,
						shipId: ship.id,
						creditor: {
							id: charterInput.creditor?.id,
						},
					},
					cargoIds: cargoList.map(it => it.id),
					charterInput: charterInput,
				},
				salesContractId: !createSalesContract ? data.salesContract?.id : undefined,
			},
		}).then((response) => {
			handleOnClose();
			refetch();

			if (createContract) {
				const charter = (response.data?.planVoyageAndLinkContractToCharters?.purchaseCharters?.[0]);
				if (charter) {
					delete charter.__typename;
					onSave(charter, createSalesContract ?? false);
				}
			} else if (createSalesContract) {
				if (referenceSpotSalesCharter) {
					const { __typename, ...salesCharter } = referenceSpotSalesCharter;
					onSave(salesCharter as CargoLinkedCharter, false);
				}
			}

		}).finally(() => {
			setLoading(false);
		});
	};

	const charters = cargos.map(cargo => cargo.charter);
	const referenceSpotSalesCharter = charters[0];

	const sameTypeCreditorDebtor =
		(charter: CargoPlanningDialogCargoFragment["charter"] | null | undefined) =>
			charter?.type === referenceSpotSalesCharter?.type
			&& charter?.creditor?.id === referenceSpotSalesCharter?.creditor?.id
			&& charter?.debtor?.id === referenceSpotSalesCharter?.debtor?.id;

	const showSalesCharter: boolean = !!referenceSpotSalesCharter && charters.every(charter => sameTypeCreditorDebtor(charter));
	const showCreateSalesContract = !!referenceSpotSalesCharter?.id && !referenceSpotSalesCharter?.contract &&
		charters.every(charter => charter?.id === referenceSpotSalesCharter.id);

	return (
		<React.Fragment>
			<FormDialog
				open={open}
				onClose={handleOnClose}
				title={intl.formatMessage({ id: "PLAN_CARGOS" })}
				form='cargoPlanningForm'
				loading={loading}
				maxWidth="md">
				<div className="cargo-planning-dialog">
					<form onSubmit={(validity) => void handleSubmit(save)(validity)} id="cargoPlanningForm">
						{hasPluginPermission(session, "angler") &&
							<PlanningChecks shipId={ship.id} cargoIds={cargos.map(x => x.id)} />}
						<Grid container spacing={3}>
							<Grid size={6}>
								<h3>{intl.formatMessage({ id: "VOYAGE" })}</h3>

								<FormGroup
									label={intl.formatMessage({ id: "VOYAGE_NUMBER" })}
									tooltip={intl.formatMessage({ id: "VOYAGE_NUMBER_TIPS" })}
									overflow="hidden">
									<Controller
										name="voyageNumber"
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormGroup>

								<h3>{intl.formatMessage({ id: "CARGOS" })}</h3>

								<div className="cargo-list">
									<List dense>
										{cargoList.map((cargo: CargoPlanningDialogCargoFragment, index: number) => (
											<React.Fragment key={index}>
												<ListItem
													secondaryAction={(
														<IconButton
															size="small"
															onClick={() => setCargoWithIdDialog(cargo.id)}>
															<Edit fontSize="small" />
														</IconButton>
													)}>

													<ListItemText
														primary={[ cargo.cargoNumber, cargo.product?.name ]
															.filter(it => Boolean(it)).join(" - ")}
														secondary={(
															<span
																className="cargo-list-item"
																style={{ justifyContent: "space-around" }}
															>
																<span>{cargo.firstLoadingAction?.location?.description}</span>
																<ArrowRightAlt fontSize="small" />
																<span>{cargo.lastDischargingAction?.location?.description}</span>
															</span>
														)} />
												</ListItem>

												<Divider />
											</React.Fragment>
										))}
									</List>
								</div>
							</Grid>

							<Grid size={6}>
								<h3>{intl.formatMessage({ id: "PURCHASE_CHARTER" })}</h3>

								{charterFields}

								{showSalesCharter && <>
									<h3>{ intl.formatMessage({ id: "SALES_CHARTER" }) }</h3>

									{showCreateSalesContract && (createContract || charterInput.id  ) && (
										<FormGroup
											label={intl.formatMessage({ id: "CREATE_CONTRACT_FOR_CHARTER" })}
											overflow="visible"
										>
											<Controller
												name="createSalesContract"
												control={control}
												render={({ field }) => (
													<Checkbox
														{...field}
														value={undefined}
														checked={field.value}
														onChange={event => field.onChange(event.target.checked)}
													/>
												)}
											/>
										</FormGroup>
									)}

									{!createSalesContract && (
										<FormGroup label={intl.formatMessage({ id: "SALE_CONTRACT" })} overflow="hidden">
											<Controller
												name="salesContract"
												control={control}
												render={({ field }) => {

													const cargoDates = getCargosDates();
													const locations = cargos.flatMap(c => c.actions)
														.map(a => a.location?.id as number);

													// referenceSpotSalesCharter should always be defined,
													//  since it is a condition in showSalesCharter; however, TypeScript
													//  keeps showing errors
													return (
														<CharterContractSelect
															{...field}
															additionalSearchOptions={{
																charterType: referenceSpotSalesCharter?.type,
																charterSubtypes: referenceSpotSalesCharter ? [ referenceSpotSalesCharter.subtype ] : undefined,
																creditors: referenceSpotSalesCharter?.creditor ? [ referenceSpotSalesCharter.creditor.id ] : undefined,
																debtors: referenceSpotSalesCharter?.debtor ? [ referenceSpotSalesCharter.debtor.id ] : undefined,
																validOver: {
																	from: moment(cargoDates[0]).valueOf(),
																	to: moment(cargoDates.at(-1)).valueOf(),
																},
																locationsOrEmpty: locations,
															}}
														/>
													);
												}}
											/>
										</FormGroup>
									)}
								</>}
							</Grid>
						</Grid>
					</form>
				</div>
			</FormDialog>

			{!!cargoWithIdDialog && (
				<CargoCreateEditDialog
					open={true}
					onClose={() => setCargoWithIdDialog(undefined)}
					cargoId={cargoWithIdDialog}
					refetch={(response) => {

						setCargoWithIdDialog(undefined);

						setCargoList([ ...cargoList ].map((cargo) => {
							if (cargo.id === response.id) {
								return response;
							} else {
								return cargo;
							}
						}));
					}}
					type={CargoDialogType.PAGE} />
			)}
		</React.Fragment>
	);
};

const PLAN_CARGO_ON_VOYAGE_MUTATION = gql(`#graphql
	mutation planCargo($input: PlanVoyageInput!, $salesContractId: Long) {
		planVoyageAndLinkContractToCharters(input: $input, salesContractId: $salesContractId) {
			id
			purchaseCharters {
				id
				charterNumber
				type
				debtor {
					id
					name
				}
				creditor {
					id
					name
				}
			}
		}
	}
`);

export default CargoPlanningDialog;
